
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAdmin } from '@/hooks/useAdmin';
import CouponList from '@/components/admin/coupons/CouponList';
import AddCouponForm from '@/components/admin/coupons/AddCouponForm';
import { Coupon } from '@/types/admin';
import { Loader2 } from 'lucide-react';

const CouponManagement = () => {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching coupons...');
      
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching coupons:', error);
        throw error;
      }

      console.log('Coupons data received:', data);

      // Type cast the discount_type to ensure it matches our union type
      const typedData = data?.map(coupon => ({
        ...coupon,
        discount_type: coupon.discount_type as "fixed" | "percentage",
        coupon_type: coupon.coupon_type as "Order" | "Booking"
      })) || [];
      
      setCoupons(typedData);
    } catch (error: any) {
      console.error('Error fetching coupons:', error);
      setError(error.message || 'Failed to load coupons');
      toast.error('Failed to load coupons: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('CouponManagement component mounted, isAdmin:', isAdmin, 'adminLoading:', adminLoading);
    if (isAdmin && !adminLoading) {
      fetchCoupons();
    }
  }, [isAdmin, adminLoading]);

  const handleAddCoupon = async (couponData: Omit<Coupon, 'id' | 'created_at'>) => {
    try {
      console.log('Adding new coupon:', couponData);
      
      const { data, error } = await supabase
        .from('coupons')
        .insert(couponData)
        .select()
        .single();

      if (error) {
        console.error('Error adding coupon:', error);
        throw error;
      }

      console.log('New coupon added:', data);

      // Type cast the returned data
      const typedCoupon = {
        ...data,
        discount_type: data.discount_type as "fixed" | "percentage",
        coupon_type: data.coupon_type as "Order" | "Booking"
      };

      setCoupons([typedCoupon, ...coupons]);
      setShowForm(false);
      toast.success('Coupon added successfully!');
    } catch (error: any) {
      console.error('Error adding coupon:', error);
      toast.error('Failed to add coupon: ' + error.message);
    }
  };

  const handleUpdateCoupon = async (couponData: Coupon) => {
    try {
      console.log('Updating coupon:', couponData);
      
      const { id, created_at, ...updateData } = couponData;
      
      const { data, error } = await supabase
        .from('coupons')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        console.error('Error updating coupon:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Failed to update coupon. Please check your permissions.');
      }

      console.log('Coupon updated:', data);

      // Type cast the returned data
      const typedCoupon = {
        ...data,
        discount_type: data.discount_type as "fixed" | "percentage",
        coupon_type: data.coupon_type as "Order" | "Booking"
      };

      setCoupons(coupons.map(c => c.id === id ? typedCoupon : c));
      setSelectedCoupon(null);
      setShowForm(false);
      toast.success('Coupon updated successfully!');
    } catch (error: any) {
      console.error('Error updating coupon:', error);
      toast.error('Failed to update coupon: ' + error.message);
    }
  };

  const handleDeleteCoupon = async (id: string) => {
    try {
      console.log('Deleting coupon with ID:', id);
      
      const { error } = await supabase
        .from('coupons')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting coupon:', error);
        throw error;
      }

      console.log('Coupon deleted successfully');
      setCoupons(coupons.filter(c => c.id !== id));
      toast.success('Coupon deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon: ' + error.message);
    }
  };

  const handleEditCoupon = (coupon: Coupon) => {
    console.log('Editing coupon:', coupon);
    setSelectedCoupon(coupon);
    setShowForm(true);
  };

  // Add debugging for admin state
  if (adminLoading) {
    console.log('Admin check is still loading...');
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </AdminLayout>
    );
  }
  
  if (!isAdmin && !adminLoading) {
    console.log('User is not an admin');
    return (
      <AdminLayout>
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-red-500">You don't have permission to access this page.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Coupon Management</h1>
          {!showForm && (
            <button 
              onClick={() => {
                setSelectedCoupon(null);
                setShowForm(true);
              }}
              className="bg-brand-blue hover:bg-brand-blue/90 text-white px-4 py-2 rounded-md flex items-center"
            >
              <span>Add New Coupon</span>
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p><strong>Error:</strong> {error}</p>
            <button 
              className="underline mt-2"
              onClick={() => fetchCoupons()}
            >
              Try again
            </button>
          </div>
        )}

        {showForm ? (
          <AddCouponForm 
            onSubmit={selectedCoupon ? handleUpdateCoupon : handleAddCoupon}
            onCancel={() => {
              setShowForm(false);
              setSelectedCoupon(null);
            }}
            initialData={selectedCoupon}
          />
        ) : (
          <CouponList 
            coupons={coupons}
            loading={loading}
            onEdit={handleEditCoupon}
            onDelete={handleDeleteCoupon}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default CouponManagement;
